import type { EnvRecord } from "./envPreflight";
import { PRODUCTION_FLAG_ENV, isProductionEnv } from "./productionFlag";

/**
 * Which client a process files its rows under (F-17), read once, one way.
 *
 * Ruled by Kabir 2026-09-12 (TA-207, closing TA-199 question 3): `cl_nekta` is
 * the real id of this deployment's client, not a fixture, and `ACKRA_CLIENT_ID`
 * is honoured by EVERY process. The API used to pin the id as a constant while
 * the worker read the variable, so setting it split one deployment across two
 * shops.
 */

export const CLIENT_ID_ENV = "ACKRA_CLIENT_ID";

/**
 * The client this deployment serves when `ACKRA_CLIENT_ID` is unset, outside
 * production only (TA-1336: under NODE_ENV=production unset is a refusal).
 */
export const DEFAULT_CLIENT_ID = "cl_nekta";

/**
 * `cl_` then lowercase letters, digits and underscores. FINDINGS.md TA-208
 * logs this as an assumption: no spec names an id format, and every id this
 * repo has ever used (production and fixtures alike) already has this shape.
 * Refusing more now is cheap to loosen; tightening once rows exist is not.
 */
const CLIENT_ID_SHAPE = /^cl_[a-z0-9_]+$/;

/**
 * A problem sentence for a malformed id, or null. Never contains the value:
 * `envPreflight`'s renderer adds it.
 */
export function validateClientId(value: string): string | null {
  if (CLIENT_ID_SHAPE.test(value)) return null;
  return (
    'is not a client id. A client id is "cl_" followed by lowercase letters, digits ' +
    "and underscores (cl_nekta). Every row is filed under it, so a value that " +
    "only looks close would file this deployment under a client that does not exist"
  );
}

export type ClientIdResolution =
  | { readonly ok: true; readonly clientId: string; readonly defaulted: boolean }
  | { readonly ok: false; readonly reason: string };

/**
 * Unset and empty both mean the default: `.env.example` ships the line
 * `ACKRA_CLIENT_ID=`, and `?? "cl_nekta"` turned that into an id of "". A set
 * value that is malformed is refused, never corrected, because an operator who
 * set it meant a specific client and a guess would file rows under the wrong one.
 */
export function resolveClientId(env: EnvRecord): ClientIdResolution {
  const raw = (env[CLIENT_ID_ENV] ?? "").trim();
  if (raw === "") {
    // TA-1336 (Kabir, 2026-10-14: one deployment per shop, Mike's is only the
    // first). The silent default filed a second shop's rows under cl_nekta
    // when its stack forgot the variable. Production refuses instead; every
    // other run (laptops, tests, e2e, the demo box) keeps the default.
    const why = clientIdProductionReason(env);
    if (why !== null) return { ok: false, reason: `${CLIENT_ID_ENV} is not set. ${why}.` };
    return { ok: true, clientId: DEFAULT_CLIENT_ID, defaulted: true };
  }
  const complaint = validateClientId(raw);
  if (complaint !== null) {
    return { ok: false, reason: `${CLIENT_ID_ENV}="${raw}" ${complaint}.` };
  }
  return { ok: true, clientId: raw, defaulted: false };
}

/**
 * TA-1336. The preflight's `requiredWhen` for ACKRA_CLIENT_ID, and the reason
 * `resolveClientId` gives under production: null outside production, so the
 * default stays an `allow` there. Worded to be followed by the preflight's own
 * ". Without it, <missingMeans>", so it ends without a full stop.
 */
export function clientIdProductionReason(env: EnvRecord): string | null {
  if (!isProductionEnv(env)) return null;
  return (
    `${PRODUCTION_FLAG_ENV}=production is set, and a production process never guesses its shop: ` +
    `every row it writes is filed under ${CLIENT_ID_ENV}, and the old default (${DEFAULT_CLIENT_ID}) ` +
    "would file a second shop's customers under the first shop's. Set it to this deployment's client id"
  );
}
